import { Component, computed, CUSTOM_ELEMENTS_SCHEMA, effect, ElementRef, inject, input, InputSignal, viewChild } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { CustomRouteService } from 'src/app/pages/custom-route/services/custom-route.service';
import { PlannerDayStateService } from 'src/app/pages/custom-route/shared/planner/services/planner-day-state.service';
import { CardStatus } from '../../../../interfaces';
import { Point, Transport } from 'src/app/pages/custom-route/interfaces/custom-route.interface';
import { BottomSheetConfig, BottomSheetData } from 'src/app/interfaces/mat.interface';
import { NewDetailsPage } from 'src/app/pages/new-details/new-details.page';
import { BreakpointsService } from 'src/app/services/breakpoints.service';
import { TransportListCardComponent } from "./components/transport-list-card/transport-list-card.component";
import { MatBottomSheetService } from 'src/app/services/mat-bottom-sheet.service';
import { HeaderComponent } from './components/header/header.component';
import { MainComponent } from './components/main/main.component';
import { DefaultImgService } from 'src/app/services';
import { CardService } from './services';


@Component({
  selector: 'app-card',
  standalone: true,
  imports: [
    TranslateModule,
    TransportListCardComponent,
    HeaderComponent,
    MainComponent
],
  templateUrl: './card.component.html',
  styleUrls: ['./card.component.scss'],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class CardComponent  {
  // Signals dom element references
  public card = viewChild.required<ElementRef>('cardRef');
  
  // Input signals
  public indexCard = input.required<number>();
  public point = input.required<Point>();
  public previousPointTitle: InputSignal<string> = input.required<string>(); // TODO: CAMBIAR A OBJETO DE TRADUCCIONES
  public transportList : InputSignal<Transport[]> = input.required<Transport[]>();
  public status: InputSignal<CardStatus> = input.required<CardStatus>();

  // Injected services
  private _customRouteService = inject(CustomRouteService);
  private _cardService = inject(CardService);
  private _defaultImgService = inject(DefaultImgService);
  private _plannerDayStateService = inject(PlannerDayStateService);
  private _breakpointsService = inject(BreakpointsService);
  private _bottomSheetService = inject(MatBottomSheetService);

  // State service signals
  private _customRouteData =  this._customRouteService.customRouteData;
  private _dayToShowIndex =  this._plannerDayStateService.dayToShowIndex;
  private _isMobile = this._breakpointsService.isMobile;
  
  // Computed properties
  private dailyCustomRouteData = computed(() => this._customRouteData().dailyRoutes[this._dayToShowIndex()]);
  protected isLocationCard = computed(() => this._cardService.isLocationCard(this.point().id));
  
  // Local properties
  protected marginTop: number;
  protected topPositionTransportList: number;
  protected isImgError: boolean = false;

  // Effects
  /**
   * Sets the height of the card element according to the duration of the point.
   */
  setTheHeightAccordingToTheDuration = effect(() => {
    const durationInMinutes = this.point().duration;
    const id = this.point().id;

    if (!durationInMinutes || !id) return;
    
    this._cardService.setHeightAccordingToDuration(
      durationInMinutes,
      this._cardService.isLocationCard(id),
      this.card().nativeElement
    );
  }, { allowSignalWrites: true });
  
  /**
   * Sets the margin-top of the card element according to the start time of the point.
   */
  setTheMarginTopAccordingToTheStartTime = effect(() => {
    const indexCard = this.indexCard();
    const startDate = this.point().startDate;

    if (indexCard === undefined || !startDate) return;
    const result = this._cardService.setMarginTopAccordingToStartTime(
      indexCard,
      startDate,
      this.dailyCustomRouteData().points,
      this.card().nativeElement
    );
    this.marginTop = result.marginTop;
    this.topPositionTransportList = result.topPositionTransportList;
  }, { allowSignalWrites: true });
  
  /**
   * Closes the bottom sheet details view when resizing the viewport to mobile or desktop.
   */
  hideDetailsViewOnResizeViewport = effect(() => {
    if (this._isMobile()) {
      this._bottomSheetService.closeAllMatBottomSheets();
    } else {
      this._bottomSheetService.closeAllMatBottomSheets();
    }
  }, {  allowSignalWrites: true });
  
  // Methods
  public async handleClickPoint(id: number): Promise<void> {
    if (this._isMobile()) {
      await this.openBottomSheetDetailsMobile(id);
    } else {
      await this.openBottomSheetDetailsDesktop(id);
    }
  }
  
  public async openBottomSheetDetailsMobile(id: number): Promise<void> {
    const type = this.point().type;
    if (!type) return;
    
    const bottomSheetConfig: BottomSheetConfig<NewDetailsPage, BottomSheetData> = {
      component: NewDetailsPage,
      data: {
        id: id,
        type: type,
        fromPointPlannerCustomRouteMobile: true,
        isModal: true,
        defaultImg: this.getDefaultImgFileName(this.indexCard())
      },
      panelClass: 'details-bottom-sheet--mobile',
    }

    await this._bottomSheetService.openMatBottomSheet(bottomSheetConfig);
  }
  
  public async openBottomSheetDetailsDesktop(id: number): Promise<void> {
    const type = this.point().type;
    if (!type) return;

    const bottomSheetConfig: BottomSheetConfig<NewDetailsPage, BottomSheetData> = {
      component: NewDetailsPage,
      data: {
        id: id,
        type: type,
        fromPointPlannerCustomRouteDesktop: true,
        isModal: true,
        defaultImg: this.getDefaultImgFileName(this.indexCard())
      },
      panelClass: 'details-bottom-sheet--desktop',
    }

    await this._bottomSheetService.openMatBottomSheet(bottomSheetConfig);
  }
  
  public isTransportListOverflow(): boolean {
    return this._cardService.isTransportListOverflow(
      this.transportList(),
      this.marginTop
    );
  }
  
  public isDuration15Minutes(): boolean {
    const duration = this.point().duration;
    if (duration === undefined) return false;
    
    return this._cardService.isDuration15Minutes(duration);
  }
  
  public handleImgError(): void {
    this.isImgError = true;
  }
  
  /**
   * Calculates the start date for the location point (id = 0) based on the previous point's end time 
   * and the total transport duration.
   */
  public getStartDate(): Date {
    const startDate = this.point().startDate;
    if (!startDate) return new Date();

    return this._cardService.getStartDateForLocationPoint(
      this.indexCard(),
      this.dailyCustomRouteData().points,
      startDate
    );
  }
  
  /**
   * @desciption Gets the default image file name for the card
   * @param index
   * @returns 
   */
  public getDefaultImgFileName(index: number): string {
    const category = this.point().category;
    const type = this.point().type;
    if (!category || !type) return '';
    
    return this._defaultImgService.getDefaultImgFileName(
      index,
      category,
      type
    );
  }
}
